/**
 * PQR boundary (tolerance) math — mirrors data360live-server `pqr_mapper.ts`:
 *   wpsLimitMin = value * (100 - boundary%) / 100
 *   wpsLimitMax = value * (100 + boundary%) / 100
 *
 * WPS Studio uses MinOfMin / MaxOfMax: apply reduction to min & avg, increase to max.
 */

function applyPercentageReduction(value: number, boundaryPercentage: number): number {
  return (value * (100 - boundaryPercentage)) / 100
}

function applyPercentageIncrease(value: number, boundaryPercentage: number): number {
  return (value * (100 + boundaryPercentage)) / 100
}

type PqrLimitsMode = 'avg' | 'minmax'

interface MinAvgMaxNumeric {
  min: number | null
  avg: number | null
  max: number | null
}

function applyToleranceToValue(
  value: number | null,
  boundary: number,
  kind: 'min' | 'avg' | 'max'
): number | null {
  if (value === null || !Number.isFinite(value)) return null
  if (boundary === 0) return value
  if (kind === 'max') return applyPercentageIncrease(value, boundary)
  return applyPercentageReduction(value, boundary)
}

function applyToleranceToSampleValue(
  value: number | null,
  boundary: number
): number | null {
  if (value === null || !Number.isFinite(value)) return null
  if (boundary === 0) return value
  return applyPercentageReduction(value, boundary)
}

function applyPqrToleranceToTriple(
  triple: MinAvgMaxNumeric,
  boundary: number,
  limitsMode: PqrLimitsMode = 'minmax'
): MinAvgMaxNumeric {
  if (boundary === 0) return { ...triple }
  if (limitsMode === 'avg') {
    return {
      min: applyToleranceToValue(triple.avg, boundary, 'min'),
      avg: applyToleranceToValue(triple.avg, boundary, 'avg'),
      max: applyToleranceToValue(triple.avg, boundary, 'max')
    }
  }
  return {
    min: applyToleranceToValue(triple.min, boundary, 'min'),
    avg: applyToleranceToValue(triple.avg, boundary, 'avg'),
    max: applyToleranceToValue(triple.max, boundary, 'max')
  }
}

function formatTolerancePercent(boundary: number): string {
  const n = Number.isFinite(boundary) ? boundary : 0
  return `${n}%`
}

export {
  applyPercentageReduction,
  applyPercentageIncrease,
  applyPqrToleranceToTriple,
  applyToleranceToSampleValue,
  applyToleranceToValue,
  formatTolerancePercent
}

export type { MinAvgMaxNumeric, PqrLimitsMode }
